import React from 'react';
import { Sparkles, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { RecommendationResult } from '../../utils/planRecommendation';
import { pricingPlans } from '../../data/pricingPlans';
interface PlanRecommendationProps {
  recommendation: RecommendationResult;
  onSelectPlan: (plan: 'Core' | 'Pro' | 'Scale') => void;
  onBack: () => void;
}
export function PlanRecommendation({
  recommendation,
  onSelectPlan,
  onBack
}: PlanRecommendationProps) {
  const recommendedPlan = pricingPlans.find(p => p.name === recommendation.plan);
  const otherPlans = pricingPlans.filter(p => p.name !== recommendation.plan);
  if (!recommendedPlan) return null;
  return <div className="space-y-6 animate-fade-in" data-id="element-2345">
      {/* Recommendation Header */}
      <div className="text-center mb-8" data-id="element-2346">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4" data-id="element-2347">
          <Sparkles className="w-4 h-4 text-[#3ab795]" data-id="element-2348" />
          <span className="text-sm font-medium text-[#3ab795]" data-id="element-2349">
            Recommended for You
          </span>
        </div>
        <h3 className="text-3xl font-bold text-white mb-3" data-id="element-2350">
          {recommendedPlan.name} Plan
        </h3>
        <p className="text-lg text-gray-300 mb-4" data-id="element-2351">
          Based on your answers, this plan is the best fit for your needs
        </p>
      </div>

      {/* Recommended Plan Card */}
      <div className="relative p-6 rounded-2xl border-2 border-[#3ab795] bg-gradient-to-br from-[#3ab795]/10 to-transparent" data-id="element-2352">
        {/* Glow effect */}
        <div className="absolute inset-0 rounded-2xl bg-[#3ab795]/5 blur-xl" data-id="element-2353"></div>

        <div className="relative" data-id="element-2354">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3ab795] text-white text-xs font-bold rounded-full mb-4" data-id="element-2355">
            <Sparkles className="w-3.5 h-3.5" data-id="element-2356" />
            Best Match
          </div>

          {/* Price */}
          <div className="mb-4" data-id="element-2357">
            <div className="flex items-baseline gap-2 mb-2" data-id="element-2358">
              <span className="text-4xl font-bold text-white" data-id="element-2359">
                ${recommendedPlan.monthlyPrice}
              </span>
              <span className="text-gray-400" data-id="element-2360">/month</span>
            </div>
            <p className="text-gray-300" data-id="element-2361">{recommendedPlan.description}</p>
          </div>

          {/* Why This Plan */}
          <div className="mb-6 p-4 bg-[#1a1a1a]/50 rounded-lg border border-[#3ab795]/20" data-id="element-2362">
            <h4 className="text-sm font-semibold text-[#3ab795] mb-3" data-id="element-2363">
              Why this plan is perfect for you:
            </h4>
            <div className="space-y-2" data-id="element-2364">
              {recommendation.reasoning.map((reason, idx) => <div key={idx} className="flex items-start gap-2" data-id="element-2365">
                  <CheckCircle className="w-4 h-4 text-[#3ab795] flex-shrink-0 mt-0.5" data-id="element-2366" />
                  <span className="text-sm text-gray-300" data-id="element-2367">{reason}</span>
                </div>)}
            </div>
          </div>

          {/* Key Features */}
          <div className="mb-6" data-id="element-2368">
            <h4 className="text-sm font-semibold text-white mb-3" data-id="element-2369">
              Key features for you:
            </h4>
            <div className="space-y-2" data-id="element-2370">
              {recommendation.relevantFeatures.map((feature, idx) => <div key={idx} className="flex items-start gap-2" data-id="element-2371">
                  <CheckCircle className="w-4 h-4 text-[#3ab795] flex-shrink-0 mt-0.5" data-id="element-2372" />
                  <span className="text-sm text-gray-300" data-id="element-2373">{feature}</span>
                </div>)}
            </div>
          </div>

          {/* CTA */}
          <button onClick={() => onSelectPlan(recommendation.plan)} className="w-full px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group" data-id="element-2374">
            <span data-id="element-2375">Select {recommendedPlan.name} Plan</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2376" />
          </button>
        </div>
      </div>

      {/* Other Plan Options */}
      <div className="relative" data-id="element-2377">
        <div className="flex items-center gap-3 mb-4" data-id="element-2378">
          <div className="flex-1 h-px bg-gray-700" data-id="element-2379"></div>
          <span className="text-sm text-gray-500" data-id="element-2380">Or choose</span>
          <div className="flex-1 h-px bg-gray-700" data-id="element-2381"></div>
        </div>

        <div className="space-y-3" data-id="element-2382">
          {otherPlans.map(plan => <button key={plan.name} onClick={() => onSelectPlan(plan.name as 'Core' | 'Pro' | 'Scale')} className="w-full p-5 rounded-xl border-2 border-gray-700 bg-[#1a1a1a] hover:border-gray-600 transition-all duration-300 text-left group" data-id="element-2383">
              <div className="flex items-start justify-between gap-4" data-id="element-2384">
                <div className="flex-1" data-id="element-2385">
                  <h4 className="text-xl font-bold text-white mb-1" data-id="element-2386">
                    {plan.name} Plan
                  </h4>
                  <div className="flex items-baseline gap-2 mb-2" data-id="element-2387">
                    <span className="text-2xl font-bold text-[#3ab795]" data-id="element-2388">
                      ${plan.monthlyPrice}
                    </span>
                    <span className="text-gray-400 text-sm" data-id="element-2389">/month</span>
                  </div>
                  <p className="text-sm text-gray-400" data-id="element-2390">{plan.description}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-[#3ab795] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" data-id="element-2391" />
              </div>
            </button>)}
        </div>
      </div>

      {/* Back Button */}
      <button onClick={onBack} className="w-full px-6 py-3 bg-[#1a1a1a] border border-gray-700 text-white font-medium rounded-lg hover:border-[#3ab795] transition-all duration-300 flex items-center justify-center gap-2 group" data-id="element-2392">
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-2393" />
        <span data-id="element-2394">Back to Questions</span>
      </button>
    </div>;
}